import { useState } from "react";
import { Crown, Sparkles, X, Loader2, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "./ui/button";
import { useAuth, formatApiError } from "../context/AuthContext";

export default function PaywallModal() {
  const { paywallModalOpen, setPaywallModalOpen, subscribe, user } = useAuth();
  const [loading, setLoading] = useState(false);

  if (!paywallModalOpen) return null;

  const handleUpgrade = async () => {
    setLoading(true);
    try {
      const { url } = await subscribe();
      window.location.href = url; // Stripe Checkout
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-[105] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4"
      data-testid="paywall-modal" role="dialog" aria-modal="true"
      onClick={() => setPaywallModalOpen(false)}
    >
      <div onClick={(e) => e.stopPropagation()}
           className="w-full max-w-lg bg-white rounded-3xl shadow-2xl border border-gray-100 overflow-hidden">
        <div className="relative bg-gradient-to-br from-amber-50 via-white to-emerald-50 p-8 pt-10">
          <button onClick={() => setPaywallModalOpen(false)}
            className="absolute top-4 right-4 p-2 rounded-full hover:bg-white/80 transition-colors"
            aria-label="Close" data-testid="paywall-close-btn">
            <X className="w-5 h-5 text-gray-500" />
          </button>

          <div className="flex items-center justify-center mb-4">
            <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-amber-400 to-amber-600 flex items-center justify-center shadow-lg shadow-amber-500/30">
              <Crown className="w-8 h-8 text-white" />
            </div>
          </div>

          <h2 className="font-playfair text-3xl font-bold text-center text-gray-900 mb-2">
            Unlock Premium Vitality Tests
          </h2>
          <p className="text-center text-gray-600 mb-6">
            Hi {user?.first_name || "there"} — upgrade to access the Men's Vitality and
            Women's Hormone assessments and get tailored protocols.
          </p>

          <div className="bg-white rounded-2xl border-2 border-amber-200 p-6 mb-6 shadow-md">
            <div className="flex items-baseline justify-center gap-1.5 mb-1">
              <span className="text-5xl font-bold text-gray-900">£4.99</span>
              <span className="text-gray-500 text-base">/ month</span>
            </div>
            <p className="text-center text-xs text-gray-500 mb-5">Auto-renews monthly · cancel anytime</p>
            <div className="space-y-2.5">
              {[
                "Men's Vitality Check (10-question scorecard)",
                "Women's Hormone Assessment (10-question scorecard)",
                "Personalized protocol email + recommendations",
                "Targeted herbal recipes for your score tier",
                "Auto-renews monthly — cancel anytime from your account",
              ].map((f) => (
                <div key={f} className="flex items-start gap-2.5">
                  <div className="w-5 h-5 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Check className="w-3 h-3 text-emerald-700" />
                  </div>
                  <span className="text-sm text-gray-700">{f}</span>
                </div>
              ))}
            </div>
          </div>

          <Button
            onClick={handleUpgrade}
            disabled={loading}
            data-testid="paywall-upgrade-btn"
            className="w-full h-14 rounded-full bg-gradient-to-r from-amber-500 to-amber-700 hover:from-amber-600 hover:to-amber-800 text-white font-semibold text-base shadow-lg shadow-amber-500/30"
          >
            {loading ? (<Loader2 className="w-5 h-5 animate-spin" />)
              : (<span className="flex items-center justify-center gap-2">
                  <Sparkles className="w-5 h-5" /> Upgrade to Premium — £4.99/mo
                </span>)}
          </Button>
          <p className="text-xs text-gray-500 text-center mt-3">
            Secure checkout via Stripe. Apple Pay, Google Pay &amp; cards accepted.
          </p>
        </div>
      </div>
    </div>
  );
}
